import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useLeads } from "@/context/LeadContext";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Loader2 } from "lucide-react";
import GuestLeadView from "@/components/leads/GuestLeadView";
import { LeadStageType } from "@/context/LeadContext";
import { phpApi } from "@/integrations/php/client";

// Simplified version of Lead Details page for guests and partners
const LeadDetails = () => {
  const { id } = useParams<{ id: string }>();
  const { getLeadById, updateLeadStage, markLeadAsViewed } = useLeads();
  const navigate = useNavigate();
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [isLoaded, setIsLoaded] = useState<boolean>(false);

  // Get the lead data
  const lead = id ? getLeadById(id) : undefined;

  useEffect(() => {
    const checkAdminStatus = async () => {
      try {
        // Check if user is logged in as admin
        const adminId = localStorage.getItem("adminId");
        const adminSession = localStorage.getItem("adminSession");
        
        setIsAdmin(!!adminId && !!adminSession);
        setIsLoaded(true);
        
        // Mark lead as viewed regardless of viewer type
        if (id) {
          await markLeadAsViewed(id);
        }
      } catch (error) {
        console.error("Error checking admin status:", error);
        setIsLoaded(true);
      }
    };
    
    checkAdminStatus();
  }, [id, markLeadAsViewed]);

  // Handle redirection for admins - IMPORTANT: keep this outside conditional rendering
  useEffect(() => {
    if (isAdmin && id && isLoaded) {
      navigate(`/admin/lead/${id}`);
    }
  }, [isAdmin, id, navigate, isLoaded]);

  const handleBack = () => {
    if (isAdmin) {
      navigate("/admin/leads");
    } else {
      navigate("/");
    }
  };

  const handleUpdateStage = async (stage: LeadStageType) => {
    if (id) {
      await updateLeadStage(id, stage);
    }
  };

  if (!isLoaded) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="flex flex-col items-center gap-2">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">Φόρτωση στοιχείων...</p>
        </div>
      </div>
    );
  }

  if (!lead) {
    return (
      <div className="flex flex-col items-center justify-center h-screen">
        <h1 className="text-2xl font-bold mb-4">Ο ενδιαφερόμενος δεν βρέθηκε</h1>
        <Button onClick={handleBack}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Επιστροφή
        </Button>
      </div>
    );
  }

  // For guests, show enhanced view
  if (!isAdmin) {
    return (
      <GuestLeadView 
        lead={lead}
        onUpdateStage={handleUpdateStage}
        showThankYou={false}
      />
    );
  }

  // For admins, we render a loading state (even though they should be redirected)
  return (
    <div className="flex items-center justify-center h-screen">
      <div className="flex flex-col items-center gap-2">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <p className="text-sm text-muted-foreground">Ανακατεύθυνση...</p>
      </div>
    </div>
  );
};

export default LeadDetails;
